import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiPlay, FiArrowRight, FiLayers, FiAward } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, Page, PageHeader, Card, Tag, ProgressBar, Segmented, EmptyState, Alert, Avatar, CAST, deckMeta, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/library.jpg', pos: 'center 28%', height: 640, opacity: 0.6 };

function lessonIdFromProgressEntry(lp) {
  if (!lp?.lesson) return null;
  return typeof lp.lesson === 'object' ? lp.lesson._id : lp.lesson;
}

function progressPercentForLesson(lesson, entry) {
  if (!entry || entry.completed) return entry?.completed ? 100 : 0;
  const dur = Number(lesson.duration) || 0;
  const spent = Number(entry.timeSpent) || 0;
  if (dur > 0) return Math.min(95, Math.round((spent / dur) * 100));
  return 15;
}

export default function LessonsList() {
  const { user, isAdmin } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedFor, setLoadedFor] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const courseId = user?.course?._id || user?.course;
  const base = isAdmin ? '/admin/lezioni' : '/lezioni';

  const progressByLessonId = useMemo(() => {
    const map = new Map();
    (progress?.lessons || []).forEach((lp) => { const id = lessonIdFromProgressEntry(lp); if (id) map.set(String(id), lp); });
    return map;
  }, [progress]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      if (!courseId) { setLessons([]); setProgress(null); setLoading(false); return; }
      try {
        const [lessonsRes, progressRes] = await Promise.all([api.get(`/lessons/course/${courseId}`), api.get('/progress/my')]);
        if (!cancelled) { setLessons(lessonsRes.data?.lessons || []); setProgress(progressRes.data?.progress || null); }
      } catch (e) {
        if (!cancelled) { setError(e.response?.data?.error || "We couldn't load the lessons. Try again in a moment!"); setLessons([]); }
      } finally { if (!cancelled) { setLoadedFor(courseId || null); setLoading(false); } }
    }
    load();
    return () => { cancelled = true; };
  }, [courseId]);

  const rows = useMemo(() => lessons.map((lesson, index) => {
    const entry = progressByLessonId.get(String(lesson._id));
    const completed = !!entry?.completed;
    const started = !completed && !!entry;
    const status = completed ? 'done' : started ? 'progress' : 'new';
    return { lesson, index, entry, completed, status, pct: progressPercentForLesson(lesson, entry), meta: deckMeta(lesson) };
  }), [lessons, progressByLessonId]);

  const visible = rows.filter(r => filter === 'all' || (filter === 'done' && r.status === 'done') || (filter === 'progress' && r.status === 'progress'));
  const doneCount = rows.filter(r => r.completed).length;
  const nextRow = rows.find(r => !r.completed) || null;

  // Also stay on the spinner while the course id has changed but its data hasn't arrived yet.
  if (loading || (courseId && loadedFor !== courseId)) return <Page hero={HERO}><LoadingSpinner message="Loading your lessons…" /></Page>;

  return (
    <Page hero={HERO}>
      <PageHeader
        kicker={user?.course?.name || 'Your course'}
        title="Lessons"
        subtitle={rows.length ? `${doneCount} of ${rows.length} completed — ${nextRow ? `next up: ${nextRow.lesson.title}` : 'you finished them all!'}` : 'Pick a lesson and start the mission.'}
        right={
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'all', label: `All (${rows.length})` },
            { value: 'progress', label: 'In progress' },
            { value: 'done', label: `Done (${doneCount})` },
          ]} />
        }
      />

      {error && <Alert>{error}</Alert>}

      {!courseId && <EmptyState icon="🎒" title="No course assigned" text="Ask your teacher to connect you to a course to see lessons here." />}
      {courseId && lessons.length === 0 && !error && <EmptyState icon="✨" title="No lessons yet" text="Come back soon — new missions are on the way!" />}
      {courseId && lessons.length > 0 && visible.length === 0 && <EmptyState icon="🦉" title="Nothing here" text={filter === 'done' ? "You haven't completed a lesson yet. Start one!" : 'No lesson in progress — open one from "All".'} />}

      {visible.length > 0 && (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
          {visible.map(({ lesson, index, completed, status, pct, meta }) => {
            const host = CAST[meta.host];
            const num = String(index + 1).padStart(2, '0');
            return (
              <motion.div key={lesson._id} variants={fadeUp} whileHover={{ y: -6 }} whileTap={{ scale: 0.99 }}>
                <Link to={`${base}/${lesson._id}`} style={{ display: 'block', height: '100%' }}>
                  <Card style={{ padding: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* cover */}
                    <div style={{ position: 'relative', aspectRatio: '16 / 8', background: BRAND.navy700, overflow: 'hidden' }}>
                      {meta.img
                        ? <img src={meta.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .6s' }} />
                        : <img src={BRAND.bg.classroom} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.5 }} />}
                      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(7,20,45,0.1) 0%, rgba(7,20,45,0.2) 50%, rgba(11,26,58,0.98) 100%)' }} />
                      <span aria-hidden style={{ position: 'absolute', left: 14, bottom: -6, fontSize: '3.6rem', fontWeight: 900, lineHeight: 1, color: BRAND.goldLight, textShadow: '0 4px 20px rgba(0,0,0,0.6)' }}>{num}</span>
                      <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 6 }}>
                        {status === 'done' && <Tag tone="success" style={{ background: 'rgba(7,20,45,0.8)' }}><FiCheckCircle size={12} /> Done</Tag>}
                        {status === 'progress' && <Tag tone="solid"><FiPlay size={12} /> In progress</Tag>}
                        {status === 'new' && <Tag tone="muted" style={{ background: 'rgba(7,20,45,0.78)', color: BRAND.ivory, borderColor: BRAND.line }}>New</Tag>}
                      </div>
                      {host && (
                        <span style={{ position: 'absolute', right: 12, bottom: 12, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 10px 4px 4px', borderRadius: 999, background: 'rgba(7,20,45,0.75)', border: `1px solid ${BRAND.line}`, fontSize: '0.74rem', fontWeight: 700 }}>
                          <Avatar cast={meta.host} size={24} /> with {host.name}
                        </span>
                      )}
                    </div>

                    {/* body */}
                    <div style={{ padding: '14px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <h2 style={{ fontSize: '1.1rem', fontWeight: 900, lineHeight: 1.25, margin: '0 0 6px' }}>{lesson.title}</h2>
                      {lesson.description && (
                        <p style={{ fontSize: '0.88rem', color: BRAND.muted, lineHeight: 1.45, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{lesson.description}</p>
                      )}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, fontSize: '0.76rem', color: BRAND.muted, fontWeight: 700 }}>
                        {meta.slides > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiLayers size={13} /> {meta.slides} slides</span>}
                        {meta.badges > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiAward size={13} /> {meta.badges} badges</span>}
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ProgressBar value={completed ? 100 : pct} height={5} style={{ flex: 1 }} />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999, background: completed ? 'transparent' : GOLD_GRADIENT, color: completed ? BRAND.goldLight : BRAND.navy900, border: completed ? `1px solid ${BRAND.gold}` : '1px solid transparent', fontWeight: 800, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {completed ? 'Replay' : status === 'progress' ? 'Resume' : 'Start'} <FiArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </Page>
  );
}
